import { getLangValue, type MenuItem, type ProductFeature } from "@/types/webMenu";

export type FeatureGroupKey =
  | "sizes"
  | "beverage"
  | "taste"
  | "sides"
  | "addons"
  | "breadsKind"
  | "saladType"
  | "attachedOffer"
  | "riceKind";

export type FeatureSelectionMode = "single" | "multi";

export type FeatureGroupDefinition = {
  key: FeatureGroupKey;
  label: string;
  enabled: boolean;
  selectionMode: FeatureSelectionMode;
  required: boolean;
  min: number;
  max: number;
  options: ProductFeature[];
};

export type SelectedCartFeature = {
  groupKey: FeatureGroupKey;
  groupLabel: string;
  optionId: string;
  optionName: string;
  optionNameAr: string;
  value: string;
  price: number;
  priceCurrency?: string;
  blendModifierId?: string;
  foodicsModifierId?: string;
  blendGroupModifierId?: string;
  foodicsGroupModifierId?: string;
  selectionMode: FeatureSelectionMode;
};

export type CartFeatureSummary = {
  groupKey: FeatureGroupKey;
  groupLabel: string;
  values: string[];
};

const FEATURE_GROUP_META: Array<{
  key: FeatureGroupKey;
  label: string;
  labelAr: string;
  selectionMode: FeatureSelectionMode;
  defaultRequired: boolean;
  options: keyof MenuItem;
  enabled: keyof MenuItem;
  min?: keyof MenuItem;
  max?: keyof MenuItem;
}> = [
  { key: "sizes", label: "Size", labelAr: "الحجم", selectionMode: "single", defaultRequired: true, options: "sizes", enabled: "hasSize" },
  { key: "beverage", label: "Beverage", labelAr: "المشروب", selectionMode: "multi", defaultRequired: true, options: "beverage", enabled: "hasBeverage", min: "minBeverageCount", max: "maxBeverageCount" },
  { key: "taste", label: "Taste", labelAr: "النكهة", selectionMode: "single", defaultRequired: true, options: "taste", enabled: "hasTaste" },
  { key: "sides", label: "Sides", labelAr: "الإضافات الجانبية", selectionMode: "multi", defaultRequired: true, options: "sides", enabled: "hasSide", min: "minSidesCount", max: "maxSidesCount" },
  { key: "addons", label: "Add-ons", labelAr: "الإضافات", selectionMode: "multi", defaultRequired: false, options: "addons", enabled: "hasAddon", min: "minAddonsCount", max: "maxAddonsCount" },
  { key: "breadsKind", label: "Bread kind", labelAr: "نوع الخبز", selectionMode: "single", defaultRequired: false, options: "breadsKind", enabled: "hasBreadsKind" },
  { key: "saladType", label: "Salad type", labelAr: "نوع السلطة", selectionMode: "single", defaultRequired: false, options: "saladType", enabled: "hasSaladType" },
  { key: "attachedOffer", label: "Attached offer", labelAr: "العرض المرفق", selectionMode: "multi", defaultRequired: false, options: "attachedOffer", enabled: "hasAttachedOffer", min: "minAttachedOfferCount", max: "maxAttachedOfferCount" },
  { key: "riceKind", label: "Rice kind", labelAr: "نوع الأرز", selectionMode: "single", defaultRequired: false, options: "riceKind", enabled: "hasRiceKind" },
];

function normalizeMinMax(raw: number | undefined, fallback: number) {
  return typeof raw === "number" && raw >= 0 ? raw : fallback;
}

function getGroupLabel(meta: (typeof FEATURE_GROUP_META)[number], language: string) {
  return language === "ar" ? meta.labelAr : meta.label;
}

export function getFeatureId(option: ProductFeature, index: number) {
  const baseLabel = option.label || option.name || option.nameAr || option.value || `option-${index}`;
  return option._id || `${baseLabel}-${index}`;
}

function getFeatureEnglishLabel(option: ProductFeature) {
  return getLangValue(option.nameLang, "en", option.label || option.name || option.value || option.nameAr || "");
}

function getFeatureArabicLabel(option: ProductFeature) {
  return getLangValue(option.nameLang, "ar", option.nameAr || option.value || option.label || option.name || "");
}

function getFeaturePrice(option: ProductFeature) {
  return Number(option.price ?? option.priceAdd ?? 0);
}

export function getMenuItemDisplayName(item: Pick<MenuItem, "name" | "nameAr" | "menuName" | "menuNameAr">, language: string) {
  return language === "ar"
    ? item.menuNameAr || item.nameAr || item.menuName || item.name
    : item.menuName || item.name || item.menuNameAr || item.nameAr;
}

export function getFeatureOptionLabel(option: ProductFeature, language: string) {
  const english = getFeatureEnglishLabel(option);
  const arabic = getFeatureArabicLabel(option);
  return language === "ar" ? arabic || english : english || arabic;
}

export function getFeatureGroups(item: MenuItem, language = "en"): FeatureGroupDefinition[] {
  return FEATURE_GROUP_META.map((meta) => {
    const options = ((item[meta.options] as ProductFeature[] | undefined) || []).filter(Boolean);
    const enabled = Boolean(item[meta.enabled]) && options.length > 0;
    const rawMin = meta.min ? Number(item[meta.min] as number | undefined) : undefined;
    const rawMax = meta.max ? Number(item[meta.max] as number | undefined) : undefined;
    
    // For single mode: min=1, max=1 (must choose exactly 1)
    // For multi mode: use provided min/max or defaults (min=0, max=options.length)
    const min = meta.selectionMode === "single" 
      ? 1 
      : normalizeMinMax(rawMin, 0);
    const max = meta.selectionMode === "single" 
      ? 1 
      : normalizeMinMax(rawMax, options.length || Number.MAX_SAFE_INTEGER);
    
    // single = always required (must choose 1)
    // multi = optional by default, but required if min > 0
    const required = enabled && (meta.selectionMode === "single" || min > 0);

    return {
      key: meta.key,
      label: getGroupLabel(meta, language),
      enabled,
      selectionMode: meta.selectionMode,
      required,
      min,
      max,
      options,
    };
  }).filter((group) => group.enabled);
}

export function mapSelectedFeatures(
  groups: FeatureGroupDefinition[],
  selectedByGroup: Record<string, string[]>,
): SelectedCartFeature[] {
  return groups.flatMap((group) => {
    const selectedIds = selectedByGroup[group.key] || [];
    return selectedIds
      .map((selectedId) => {
        const matched = group.options.find((option, index) => getFeatureId(option, index) === selectedId);
        return matched ? { option: matched, optionId: selectedId } : null;
      })
      .filter((entry): entry is { option: ProductFeature; optionId: string } => Boolean(entry))
      .map(({ option, optionId }) => ({
        groupKey: group.key,
        groupLabel: group.label,
        optionId,
        optionName: getFeatureEnglishLabel(option),
        optionNameAr: getFeatureArabicLabel(option),
        value: option.value || getFeatureEnglishLabel(option),
        price: getFeaturePrice(option),
        priceCurrency: option.priceCurrency,
        blendModifierId: option.blendModifierId,
        foodicsModifierId: option.FoodicsModifierId,
        blendGroupModifierId: option.blendGroupModifierId,
        foodicsGroupModifierId: option.FoodicsGroupModifierId,
        selectionMode: group.selectionMode,
      }));
  });
}

export function calculateSelectedFeaturesPrice(selectedFeatures: SelectedCartFeature[]) {
  return selectedFeatures.reduce((sum, feature) => sum + Number(feature.price || 0), 0);
}

export function validateFeatureSelections(
  groups: FeatureGroupDefinition[],
  selectedByGroup: Record<string, string[]>,
  language = "en",
) {
  const errors: Record<string, string> = {};
  const isAr = language === "ar";

  groups.forEach((group) => {
    const selectedCount = (selectedByGroup[group.key] || []).length;

    if (group.selectionMode === "single") {
      if (group.required && selectedCount !== 1) {
        errors[group.key] = isAr 
          ? `الرجاء اختيار ${group.label}.`
          : `Please choose 1 ${group.label.toLowerCase()}.`;
      } else if (!group.required && selectedCount > 1) {
        errors[group.key] = isAr
          ? `يمكنك اختيار واحد فقط من ${group.label}.`
          : `You can choose only 1 ${group.label.toLowerCase()}.`;
      }
      return;
    }

    if (selectedCount < group.min) {
      errors[group.key] = isAr
        ? `اختر ${group.min} على الأقل من ${group.label}.`
        : `Choose at least ${group.min} option${group.min === 1 ? "" : "s"} for ${group.label.toLowerCase()}.`;
      return;
    }

    if (selectedCount > group.max) {
      errors[group.key] = isAr
        ? `اختر ${group.max} كحد أقصى من ${group.label}.`
        : `Choose no more than ${group.max} option${group.max === 1 ? "" : "s"} for ${group.label.toLowerCase()}.`;
    }
  });

  return errors;
}

export function buildFeatureSummary(selectedFeatures: SelectedCartFeature[], language: string): CartFeatureSummary[] {
  const grouped = new Map<string, CartFeatureSummary>();

  selectedFeatures.forEach((feature) => {
    const key = feature.groupKey;
    if (!grouped.has(key)) {
      grouped.set(key, { groupKey: feature.groupKey, groupLabel: language === "ar" ? feature.groupLabel : feature.groupLabel, values: [] });
    }

    grouped.get(key)!.values.push(language === "ar" ? feature.optionNameAr || feature.optionName : feature.optionName || feature.optionNameAr);
  });

  return Array.from(grouped.values());
}

export function buildLegacyCustomization(selectedFeatures: SelectedCartFeature[]) {
  const size = selectedFeatures.find((feature) => feature.groupKey === "sizes")?.optionName || null;
  const extras = selectedFeatures
    .filter((feature) => feature.groupKey !== "sizes")
    .map((feature) => feature.optionName)
    .filter(Boolean);

  return { size, extras };
}

export function serializeSelectedFeatures(selectedFeatures: SelectedCartFeature[]) {
  return JSON.stringify(
    [...selectedFeatures]
      .map((feature) => ({
        ...feature,
        price: Number(feature.price || 0),
      }))
      .sort((left, right) => `${left.groupKey}:${left.optionId}:${left.optionName}`.localeCompare(`${right.groupKey}:${right.optionId}:${right.optionName}`)),
  );
}

export function getGroupRuleText(group: FeatureGroupDefinition, language = "en") {
  const isAr = language === "ar";
  
  // Single selection mode: always required, must choose exactly 1
  if (group.selectionMode === "single") {
    return isAr ? "مطلوب · اختر 1" : "Required · choose 1";
  }

  // Multi selection mode
  const hasMin = group.min > 0;
  const hasMax = Number.isFinite(group.max) && group.max < Number.MAX_SAFE_INTEGER;
  
  if (hasMin && hasMax) {
    // Required with min and max
    return isAr 
      ? `مطلوب · الحد الأدنى ${group.min} · الحد الأقصى ${group.max}`
      : `Required · min ${group.min} · max ${group.max}`;
  }
  
  if (hasMin) {
    // Required with min only
    return isAr 
      ? `مطلوب · اختر ${group.min} على الأقل`
      : `Required · choose at least ${group.min}`;
  }
  
  if (hasMax) {
    // Optional with max only
    return isAr 
      ? `اختياري · الحد الأقصى ${group.max}`
      : `optional · max ${group.max}`;
  }
  
  // Optional with no limits
  return isAr ? "اختياري" : "optional";
}
